import type { RouteDef } from '../../app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * The "Generations of Excellence" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/generations-of-excellence-5-steps-to-transform-and-sustain-your-family-business/`),
 * sent as five screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı
 * tastamam şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's:
 * the two-column hero with the stairway photograph beside it, the grey "What
 * You'll Learn" card with the pair of cover mockups and the five bolded
 * lessons, the "Why This Ebook?" card with the stacked copies, the index band,
 * the centred "Don't Miss Out!" ask and the "About Us" close. The copy is that
 * page's copy, verbatim, with the one substitution asked for in the same
 * message and described below.
 *
 * **The index band names DAIMI, not the Future Readiness Index.** Asked for
 * with the design: *"Future readiness yerine DAIMI index olsun genelde
 * kullandığımız uygun hali ile"*. The reference's band sells an "FRI" that this
 * site does not offer under that name; the index it does offer is the **Digital
 * & AI Maturity Index (DAIMI)**, which is how `pages/strategy-consulting.ts`
 * and the footer write it, and its Learn more goes to `LINKS.daimi` rather than
 * to a page that would have to be invented. Everything else in the sentence —
 * readiness for what is ahead, strengths and areas for improvement — is the
 * reference's own.
 *
 * Eighth e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail, Generative AI and Transforming
 * Pain into Power, and it follows `ebook-transforming-pain-into-power.ts` in
 * every place the reference and this site disagree — the same three deliberate
 * departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "5 STEPS TO TRANSFORM AND SUSTAIN YOUR
 *    FAMILY BUSINESS" label. Every other hero here is that shape, and the words
 *    are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do, asked for on 2026-08-28 (*"en aşağıda
 *    önceden konuştuğumuz gibi testimonial template ini kullanalım"*). The card
 *    is Arzum's: of the twenty in `content/testimonials.ts` it is the one about
 *    a **family-founded company's own inner dynamics and distinctive business
 *    model** being understood before anything was measured, which is the
 *    anxiety this book is written for. Its words are the Hall of Fame's; that
 *    file is where a change to them belongs.
 *
 * **The slug is confirmed, and it is the long one.** `content/ebooks.ts`
 * derived `generations-of-excellence` from the part of the title before the
 * colon; the screenshots were captured from the full descriptive address and
 * carry it in their filenames, so the catalogue entry was corrected to match
 * and that e-book's slug `check` closed with it.
 */
export const route: RouteDef = {
  id: 'ebookGenerationsOfExcellence',
  path: 'impact-labs/ebooks/generations-of-excellence-5-steps-to-transform-and-sustain-your-family-business',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket rather than in `public/` — the same
 * object `content/ebooks.ts` already lists for this e-book's card, spelled out
 * here because this page needs it on four buttons and that file's `pdf()` helper
 * is private to the catalogue. A page's own address and file are local constants
 * in its own module, not keys in `content/pages/links.ts`.
 */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '0ca92deb-b73d-405f-b435-b86b676332de-Generations%20of%20Excellence.pdf'

export const ebookGenerationsOfExcellence: WrittenPage = {
  navLabel: 'Generations of Excellence',
  title: 'Generations of Excellence',
  description:
    'Five steps to transform and sustain your family business, with the strategies that keep a legacy growing across generations. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: '5 Steps to Transform and Sustain Your Family Business',
      headline: 'Generations of Excellence',
      body: [
        'Family-owned businesses have a unique legacy and heritage that deserve to be preserved '
          + 'and grown. Our comprehensive ebook provides the essential strategies to ensure your '
          + 'family business thrives for generations to come.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-artwork.webp',
        alt:
          'The e-book’s cover photograph: a parent kneeling at the top of a wide flight of steps, '
          + 'pointing something out to a child against a bright sky.',
      },
      // The photograph is square and the site's hero box is 3:2, which would
      // take the sky off the top and the steps off the bottom — and the steps
      // are the picture.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover photograph and the two mockups anybody has sent; every other hero '
        + 'on the site is 2560px, so this one is painted at roughly its own width on '
        + 'a laptop with nothing left over for a retina screen. The only other copy '
        + 'in the repository is the 400×400 thumbnail the WordPress import carried, '
        + 'which is smaller still. If a bigger export exists wherever the cover was '
        + 'made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/generations-of-excellence-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'What You’ll Learn',
      points: [
        'Embrace Change: Discover how to navigate and leverage market dynamics to stay ahead.',
        'Build Sustainability: Integrate eco-friendly practices that enhance your reputation and '
          + 'ensure long-term viability.',
        'Foster Innovation: Create a culture that encourages creativity, collaboration, and '
          + 'continuous improvement.',
        'Measure and Monitor: Utilize strategic tools to track your progress and ensure '
          + 'successful transformation.',
        'Prepare Future Leaders: Learn how to mentor and develop the next generation to secure '
          + 'your business’s legacy.',
      ],
      ctas: [{ label: 'Download the Report', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-covers.webp',
        alt:
          'Two copies of Generations of Excellence, one behind the other, on a white background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why This Ebook?',
      body: [
        'In today’s fast-paced and ever-changing business environment, staying competitive '
          + 'requires more than just tradition. This ebook offers actionable insights and '
          + 'real-world case studies to help you transform and sustain your family business. '
          + 'Whether you’re looking to innovate, embrace sustainability, or prepare the next '
          + 'generation for leadership, this guide is your roadmap to generational excellence.',
      ],
      ctas: [{ label: 'Download the Report', href: PDF }],
      photo: {
        src: '/photo/generations-of-excellence-ebook-stack.webp',
        alt: 'A stack of printed copies of Generations of Excellence seen from above.',
      },
    },
    {
      // The reference's "Stay Ahead with the Future Readiness Index" band, with
      // the index this site actually sells in its place — see the note at the
      // top of the file.
      kind: 'prose',
      align: 'center',
      heading: 'Stay Ahead with the Digital & AI Maturity Index',
      sub:
        'Ensure your business is prepared for the challenges and opportunities ahead. The Digital '
        + '& AI Maturity Index (DAIMI) provides valuable insights into your business’s readiness '
        + 'for the future, helping you identify strengths and areas for improvement.',
      cta: { label: 'Learn more', href: LINKS.daimi },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out! Download Your Free Ebook Now',
      sub:
        'Join hundreds of family businesses that have successfully transformed and sustained '
        + 'their legacies. Download your free copy today!',
      cta: { label: 'Download the Ebook', href: PDF },
    },
    {
      kind: 'prose',
      heading: 'About Us',
      paragraphs: [
        'We are committed to helping family businesses thrive. With decades of experience and a '
          + 'wealth of resources, we provide the guidance and support needed to navigate the '
          + 'complexities of today’s business world. Let us help you create a lasting legacy for '
          + 'your family business.',
        'Together, we can build a future where family businesses continue to thrive and make a '
          + 'positive impact on the world. For more information, contact us today.',
      ],
      cta: { label: 'Contact us', href: '#contact' },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Arzum card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'The digital maturity measurement process was a smooth experience for us. The '
            + 'Digitopia delivery team took their time to understand Arzum’s inner dynamics '
            + 'before starting the first initiative. They internalized our company’s distinctive '
            + 'business model and managed the process accordingly and we felt this difference '
            + 'from the beginning of the first session to the very last one. Applying a '
            + 'coach-like style, Digitopia empowered us to evaluate ourselves in a very short '
            + 'time by asking the right questions to the right people. Focusing on six different '
            + 'dimensions, they conducted separate studies for each vertical.',
          source: 'Arif Emre Unal, Executive Board Member, CIO & COO',
          company: 'Arzum',
          logo: { name: 'Arzum', src: '/logo/arzumcard.png' },
          portrait: { src: '/photo/portrait-arif-emre-unal.png', alt: 'Arif Emre Unal' },
        },
      ],
    },
  ],
}
